import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Flex, Modal, Spinner, Typography } from '@fullstory/components';
import { useNotifications } from '@fullstory/react-hooks';
import { AppDispatch, fullstorySelectors, UserOverlayRequestActions } from '@fullstory/redux';
import { useDispatch, useSelector } from 'react-redux';
import { int64 } from '@fullstory/proto/google/protobuf';
import { IndividualDetails } from '@fullstory/proto/fs/services/eraser';
import { FormattedMessage, useIntl } from 'react-intl';

const { getOrgId } = fullstorySelectors;

// Holds all the state for controlling / fetching data for the delete modal
export const DeleteUserModalContainer: React.FC<
  {
    userId: string;
    appId: string;
  } & Pick<React.ComponentProps<typeof DeleteUserModalView>, 'onCloseSignal' | 'userName'>
> = ({ userId, appId, userName, onCloseSignal }) => {
  const orgId = useSelector(getOrgId);
  const dispatch = useDispatch<AppDispatch>();
  const notifications = useNotifications();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [bulkyInfo, setBulkyInfo] = useState<IndividualDetails | null>(null);
  const [deleted, setDeleted] = useState(false);
  const intl = useIntl();
  const deleteRequest = useCallback(() => {
    const action = appId
      ? UserOverlayRequestActions.eraserDeleteIndv({
          orgId,
          uid: appId,
        })
      : UserOverlayRequestActions.eraserDeleteUser({
          orgId,
          userId: userId as int64,
        });
    dispatch(action)
      .then(() => {
        notifications?.success(
          intl.formatMessage({ defaultMessage: 'User successfully deleted.', id: 'DGt8JL' }),
        );
      })
      .catch(error => {
        const errorMessage =
          error?.safeMessage && (error?.safeMessage as string)
            ? (error.safeMessage as string)
            : intl.formatMessage({
                defaultMessage: 'Something went wrong. Please try again.',
                id: 'W/5hwr',
              });
        notifications?.error(errorMessage);
      });
  }, [appId, orgId, userId, dispatch, notifications, intl]);
  useEffect(() => {
    setLoading(true);
    setError(false);
    const action = appId
      ? UserOverlayRequestActions.eraserQueryIndv({
          orgId,
          uid: appId,
        })
      : UserOverlayRequestActions.eraserQueryUser({
          orgId,
          userId: userId as int64,
        });
    dispatch(action)
      .then(data => {
        setBulkyInfo(data);
        setLoading(false);
        setError(false);
      })
      .catch(() => {
        notifications?.error(
          intl.formatMessage({ defaultMessage: 'Failed to fetch user data', id: '2bHWXL' }),
        );
        setLoading(false);
        setError(true);
      });
  }, [dispatch, userId, appId, notifications, setLoading, setError, setBulkyInfo, orgId, intl]);
  return (
    <DeleteUserModalView
      data-component="DeleteUserModalContainer"
      onCloseSignal={onCloseSignal}
      loading={loading}
      error={error}
      isDeleting={bulkyInfo?.existingOperation || false}
      numSessions={bulkyInfo?.numSessions || 0}
      userName={userName}
      onConfirmDelete={() => {
        console.log('deleted', deleted);

        if (!deleted) {
          deleteRequest();
          setDeleted(true);
          onCloseSignal();
        }
      }}
    />
  );
};

// The plain view that renders the modal
export const DeleteUserModalView: React.FC<{
  onCloseSignal: () => void;
  onConfirmDelete: () => void;
  userName: string;
  numSessions: number;
  isDeleting: boolean;
  loading: boolean;
  error: boolean;
}> = ({ userName, onCloseSignal, onConfirmDelete, numSessions, isDeleting, loading, error }) => (
  <Modal data-component="DeleteUserModalView" onCloseSignal={onCloseSignal} size="small">
    <Modal.Header>
      <FormattedMessage
        defaultMessage="Delete User: {userName}"
        id="FMLd0O"
        values={{
          userName,
        }}
      />
    </Modal.Header>
    {error ? (
      <Modal.Body>
        <Typography variant="body2">
          <FormattedMessage
            defaultMessage="An unexpected error occured, please try again or contact support@fullstory.com"
            id="0F2T7K"
          />
        </Typography>
      </Modal.Body>
    ) : loading ? (
      <Flex alignItems="center" justifyContent="center">
        <Box padding={24}>
          <Spinner />{' '}
        </Box>
      </Flex>
    ) : (
      <Modal.Body>
        <Typography variant="body2">
          <FormattedMessage defaultMessage="Deleting this user will:" id="NrE6b3" />
        </Typography>
        <ul>
          <li>
            <Typography variant="body2">
              <FormattedMessage defaultMessage="Permanently erase" id="Dg40Di" />
            </Typography>
            &nbsp;
            <Typography variant="body2" fontWeight="bold">
              <FormattedMessage
                defaultMessage="{count, plural, one {# session} other {# sessions}}"
                id="jdbsrn"
                values={{ count: numSessions }}
              />
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <FormattedMessage
                defaultMessage="Remove any data record of this user in your account."
                id="OSUO0F"
              />
            </Typography>
          </li>
        </ul>
      </Modal.Body>
    )}
    <Modal.Footer>
      {!loading && (
        <>
          {isDeleting ? (
            <Button variant="outline" fullWidth disabled>
              <FormattedMessage defaultMessage="User is currently being deleted" id="dPqTMK" />
            </Button>
          ) : (
            <Button variant="fill-destructive" fullWidth onClick={onConfirmDelete}>
              <FormattedMessage
                defaultMessage="Delete {userName}"
                id="1NygZ5"
                values={{
                  userName,
                }}
              />
            </Button>
          )}
          <Flex justifyContent="center">
            <Box padding={12}>
              <Typography variant="body3" color="neutrals-n7">
                <FormattedMessage
                  defaultMessage="You will receive an email when deletion is complete. This action is permanent and
                  cannot be undone."
                  id="femP0t"
                />
              </Typography>
            </Box>
          </Flex>
        </>
      )}
    </Modal.Footer>
  </Modal>
);
